import { useSelector } from "react-redux"
import { RootState } from "Shared/store/rootState.interface"
import { CreditorInterface, Employee, Expense, Vendor } from "@finway-group/shared/lib/models"
import store from "Shared/store"
import { useEmployees, useLoggedInEmployee } from "./employee.hooks"
import { createCreditorObject } from "Shared/utils/vendor.utils"
import { shouldExpenseIncludeEmployeesAsVendor, shouldExpenseIncludeVendors } from "Features/pages/expenses/expenseUtils"
import { FINWAY_ADMIN_EMAIL } from "Shared/config/consts"

export const useVendors = (excludeDeleted: boolean = false, searchValue?: string) => {
    const vendors = useSelector(({ vendors }: RootState) => vendors.items)
    return filterVendors(vendors, excludeDeleted, searchValue)
}

export const getVendorsFromStore = (excludeDeleted: boolean = false, searchValue?: string) => {
    const vendors = store.getState().vendors.items
    return filterVendors(vendors, excludeDeleted, searchValue)
}

const filterVendors = (vendors: any[], excludeDeleted: boolean = false, searchValue?: string) => {
    if (excludeDeleted) {
        vendors = vendors?.filter((vendor: Vendor) => !vendor.deleted)
    }

    if (searchValue) {
        vendors = vendors?.filter(
            (vendor: Vendor) => vendor.name.toLowerCase().includes(searchValue.toLowerCase()) || vendor.creditorNumber?.toString().includes(searchValue.toLowerCase())
        )
    }

    return vendors.map((vendor: any) => new Vendor(vendor)).sort((a: Vendor, b: Vendor) => a.name?.localeCompare(b.name))
}

export const useVendorById = (id: string) => useVendors().find((vendor: Vendor) => vendor.id === id)

export const useVendor = () => new Vendor(useSelector(({ vendors }: RootState) => vendors.item))

/*
 * CREDITOR-HOOKS
 */

const useAllCreditors = () => useSelector(({ creditors }: RootState) => creditors.items)

export const useCreditorById = (creditorId: string) => useAllCreditors().get(creditorId)

export const useCreditors = (canSeeEveryEmployee: boolean, includeVendors: boolean, includeEmployees: boolean) => {
    const res: CreditorInterface[] = []

    useAllCreditors().forEach((creditor) => {

        // only add vendors if requested
        if (includeVendors && creditor.source instanceof Vendor && !creditor.source.deleted) {
            res.push(creditor)
            return
        }

        // only add all employees if requested
        if (includeEmployees && canSeeEveryEmployee && creditor.source instanceof Employee && creditor.source.email !== FINWAY_ADMIN_EMAIL && !creditor.source.activeCompanyProfile?.deleted) {
            res.push(creditor)
            return
        }
    })
    // add only self
    if (includeEmployees && !canSeeEveryEmployee) res.push(createCreditorObject(useLoggedInEmployee()))

    return res
}

export const useFilteredCreditorsForExpense = (canSeeEveryEmployee: boolean, expense: Expense) =>
    useCreditors(canSeeEveryEmployee, shouldExpenseIncludeVendors(expense), shouldExpenseIncludeEmployeesAsVendor(expense))
